import { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import LaunchIcon from "@mui/icons-material/Launch";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { misc, neutral } from "app/themes/colors";
import type { Patient } from "services/types/patients.type";
import { formatMoney } from "utils/common";

import { LILAC, PLUM_600, PLUM_900 } from "./dialstack-softphone-widget.styles";
import {
  activeTags,
  appointmentContext,
  balanceDue,
  pendingFormsCount,
  providerLine,
  providerNames
} from "./softphone-patient-context.util";
import SoftphonePatientTags from "./softphone-patient-tags.component";

// A row's tags are colour dots beside the name; past this many they fold
// into a "+N".
const MAX_ROW_TAG_DOTS = 4;

// Things the front desk can resolve while the patient is on the phone.
function attentionFlags(patient: Patient) {
  const flags: string[] = [];
  const balance = balanceDue(patient);
  if (balance > 0) {
    flags.push(`${formatMoney(balance, 2)} balance due`);
  }
  const pendingForms = pendingFormsCount(patient);
  if (pendingForms > 0) {
    flags.push(
      `${pendingForms} onboarding form${pendingForms > 1 ? "s" : ""} incomplete`
    );
  }
  return flags;
}

// One patient inside a softphone call card (incoming pop or outgoing call):
// profile link plus the call-time context (provider, tags, next appointment,
// balance, forms).
// Fed by the INCOMING_CALL_PATIENTS_QUERY field set. `onAttach` adds a "This
// caller" button for shared-number matches, attaching the call to the patient
// who is actually on the line.
export default function SoftphonePatientRow({
  patient,
  onAttach,
  isAttaching = false
}: {
  patient: Patient;
  onAttach?: (patient: Patient) => void;
  isAttaching?: boolean;
}) {
  const navigate = useNavigate();
  const context = appointmentContext(patient);
  const flags = attentionFlags(patient);
  const tags = activeTags(patient);
  const providers = providerNames(patient);
  const href = `/patients/${patient.id}`;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(href);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
      <Avatar
        src={patient.photo || undefined}
        sx={{ width: 28, height: 28, fontSize: 13 }}
      >
        {(patient.first_name?.[0] ?? "") + (patient.last_name?.[0] ?? "")}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/* Wraps, so a long name keeps its width and the dots drop below it. */}
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 0.75 }}
        >
          <Link
            href={href}
            onClick={handleClick}
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 14,
              fontWeight: 600,
              color: PLUM_600
            }}
          >
            {patient.fullName ||
              `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim()}
            <LaunchIcon sx={{ fontSize: 14 }} />
          </Link>
          <SoftphonePatientTags tags={tags} limit={MAX_ROW_TAG_DOTS} dots />
        </Stack>
        {providers.length > 0 && (
          <Typography
            data-testid="row-provider"
            sx={{ fontSize: 12, color: neutral[800] }}
          >
            Provider:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: PLUM_900 }}>
              {providerLine(providers)}
            </Box>
          </Typography>
        )}
        {context && (
          <>
            <Typography sx={{ fontSize: 12, color: neutral[800] }}>
              {context.when}
            </Typography>
            {context.details && (
              <Typography sx={{ fontSize: 12, color: neutral[800] }}>
                {context.details}
              </Typography>
            )}
          </>
        )}
        {flags.length > 0 && (
          <Typography
            sx={{ fontSize: 12, color: misc.warningText, fontWeight: 600 }}
            data-testid="incoming-call-attention-flags"
          >
            {flags.join(" · ")}
          </Typography>
        )}
      </Box>
      {onAttach && (
        <Button
          size="small"
          variant="outlined"
          disabled={isAttaching}
          onClick={() => onAttach(patient)}
          data-testid="softphone-patient-attach"
          sx={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 0.25,
            minWidth: 0,
            whiteSpace: "nowrap",
            color: PLUM_600,
            borderColor: LILAC,
            "&:hover": { borderColor: PLUM_600, bgcolor: misc.lightLavender }
          }}
        >
          This caller
        </Button>
      )}
    </Stack>
  );
}
